import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../../../types/product";
import { useCart } from "../../../../context/CartContext";

interface Props {
  currentProduct: Product;
  bundleProduct: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onBundleAdded: () => void;
}

const BUNDLE_DISCOUNT = 0.1; // 10% off

export default function BundleSavePopover({
  currentProduct,
  bundleProduct,
  isOpen,
  onClose,
  onBundleAdded,
}: Props) {
  const { addItem } = useCart();
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!bundleProduct || !isOpen) return null;

  const discountedPrice = Math.round(bundleProduct.price * (1 - BUNDLE_DISCOUNT));
  const savings = bundleProduct.price - discountedPrice;
  const bundleTotal = currentProduct.price + discountedPrice;

  const categoryLabel = (cat: string) =>
    cat === "solar" ? "Solar & Battery" : cat === "gas" ? "Gas Generator" : "Whole-Home System";

  const pairingReason = (() => {
    const cur = currentProduct.category;
    const bun = bundleProduct.category;
    if (cur === "solar" && bun === "gas") return "Keep the lights on even when clouds block solar — the perfect 24/7 backup combo.";
    if (cur === "gas" && bun === "solar") return "Add silent solar charging for overnight use — zero fuel, zero noise.";
    if (cur === "whole-home" && bun === "solar") return "Pair portable solar for RV trips, camping, and mobile power needs.";
    if (cur === "whole-home" && bun === "gas") return "A portable unit keeps critical devices running during whole-home system maintenance.";
    return "Customers who buy these together save big on their complete power system.";
  })();

  const handleAddBundle = () => {
    addItem(
      {
        id: bundleProduct.id,
        name: bundleProduct.name,
        brand: bundleProduct.brand,
        price: discountedPrice,
        image: bundleProduct.image,
      },
      1
    );
    onBundleAdded();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
        style={{ animation: "fadeIn 0.15s ease" }}
        onClick={(e) => {
          if (e.target === overlayRef.current) onClose();
        }}
      >
        {/* Popover card */}
        <div
          className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden"
          style={{ animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}
        >
          {/* Top accent bar */}
          <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />

          {/* Header */}
          <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                  Bundle &amp; Save
                </span>
                <span className="text-xs text-emerald-600 font-semibold">
                  Save ${savings.toLocaleString()} today only
                </span>
              </div>
              <h2 className="text-base font-extrabold text-gray-900 leading-snug">
                Complete your power system
              </h2>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{pairingReason}</p>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Bundle products visual */}
          <div className="px-6 pb-4">
            <div className="bg-gray-50 rounded-xl p-4">
              {/* Cart item (already added) */}
              <div className="flex items-center gap-3 mb-3">
                <div className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-white border border-gray-100">
                  <img
                    src={currentProduct.image}
                    alt={currentProduct.name}
                    className="w-full h-full object-contain object-top p-1"
                  />
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-emerald-600 font-semibold">✓ In your cart</p>
                  <p className="text-sm font-bold text-gray-900 line-clamp-1">{currentProduct.name}</p>
                  <p className="text-xs text-gray-500">{currentProduct.brand}</p>
                </div>
                <p className="text-sm font-bold text-gray-900 whitespace-nowrap">
                  ${currentProduct.price.toLocaleString()}
                </p>
              </div>

              {/* Plus connector */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 flex justify-center shrink-0">
                  <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-add-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-amber-600 font-bold whitespace-nowrap">Bundle deal</span>
              </div>

              {/* Bundle suggestion product */}
              <div className="flex items-center gap-3">
                <Link
                  to={`/shop/${bundleProduct.id}`}
                  onClick={onClose}
                  className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-white border border-amber-200 hover:border-amber-400 transition-colors cursor-pointer"
                >
                  <img
                    src={bundleProduct.image}
                    alt={bundleProduct.name}
                    className="w-full h-full object-contain object-top p-1"
                  />
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1 rounded">
                    -{Math.round(BUNDLE_DISCOUNT * 100)}%
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                    {categoryLabel(bundleProduct.category)}
                  </span>
                  <p className="text-sm font-bold text-gray-900 line-clamp-1">{bundleProduct.name}</p>
                  <p className="text-xs text-gray-500">{bundleProduct.brand}</p>
                </div>
                <div className="text-right whitespace-nowrap">
                  <p className="text-sm font-extrabold text-red-600">${discountedPrice.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 line-through">${bundleProduct.price.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Bundle total */}
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-xs text-gray-500">Bundle total</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 line-through">
                  ${(currentProduct.price + bundleProduct.price).toLocaleString()}
                </span>
                <span className="text-base font-extrabold text-gray-900">
                  ${bundleTotal.toLocaleString()}
                </span>
                <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  Save ${savings.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex flex-col gap-2.5">
            <button
              onClick={handleAddBundle}
              className="w-full py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-cart-line"></i>
              </div>
              Add {bundleProduct.brand} {bundleProduct.category === "solar" ? "Solar" : bundleProduct.category === "gas" ? "Generator" : "System"} — Save ${savings.toLocaleString()}
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              No thanks, just the one item
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
